/**
 * ISSUE-1188. One Game log category (Scores UX spec v1.0 sections 7.5 and 9,
 * and the v1 game log: sportsmet_flutter
 * lib/feature/playerdetail/ui/component/gamelog/gamelog_tab.dart, one card,
 * frozen game column, stat columns). The structure is BoxScoreTable's: the
 * game column is sticky and capped at half the card while the stat columns
 * scroll sideways inside it, so the page never scrolls sideways at phone
 * width. Each row is one link to game detail. Every cell is the server's text.
 */
import { cn, FOCUS_RING } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useRef } from "react";

import { ChevronRightIcon } from "../../icons/glyphs";
import { GAME_SECTION_CARD_CLASS } from "../gameDetail/GameSectionCard";
import { useIsScrollEndClipped } from "../gameDetail/scrollEndCue";
import {
  gameLogCellText,
  playerGameLogRowText,
  type typePlayerGameLogGame,
  type typePlayerGameLogTable,
} from "./playerSeasonView";

/** The sticky game column: never wider than half the card. */
const GAME_COLUMN_CLASS =
  "sticky left-0 z-10 w-40 max-w-1/2 md:w-56 pr-2 pl-3 text-left";
const ROW_RULE_CLASS = "border-b border-hairline";
const STAT_CELL_CLASS =
  "h-touch-target min-w-12 px-2 text-center tabular-nums whitespace-nowrap";

export const PlayerGameLogTable = (props: {
  table: typePlayerGameLogTable;
  games: readonly typePlayerGameLogGame[];
}): ReactNode => {
  const { table, games } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const isEndClipped = useIsScrollEndClipped(scrollRef);
  return (
    <section
      data-testid="player-game-log-table"
      data-table-key={table.key}
      data-game-section-card=""
      className={cn(GAME_SECTION_CARD_CLASS, "gap-0")}
    >
      <div className="relative">
        <div
          ref={scrollRef}
          data-testid="player-game-log-scroll"
          className="overflow-x-auto"
        >
          <table className="w-full border-separate border-spacing-0 text-body">
            <caption className="sr-only">{`${table.label} game log`}</caption>
            <thead>
              <tr>
                <th
                  scope="col"
                  data-testid="player-game-log-header"
                  className={cn(
                    GAME_COLUMN_CLASS,
                    ROW_RULE_CLASS,
                    "h-touch-target bg-surface text-body-strong text-ink",
                  )}
                >
                  {table.label}
                </th>
                {table.columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn(
                      STAT_CELL_CLASS,
                      ROW_RULE_CLASS,
                      "bg-surface text-small font-semibold text-ink-tertiary",
                    )}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row) => {
                const game = games.find((g) => g.eventId === row.eventId);
                if (game === undefined) return null;
                const { primary, secondary } = playerGameLogRowText(game);
                return (
                  <tr
                    key={row.eventId}
                    className="relative"
                    data-testid="player-game-log-row"
                    data-event-id={row.eventId}
                  >
                    <th
                      scope="row"
                      data-testid="player-game-log-game-cell"
                      className={cn(
                        GAME_COLUMN_CLASS,
                        ROW_RULE_CLASS,
                        "h-touch-target bg-surface font-normal",
                      )}
                    >
                      <Link
                        to="/scores"
                        search={{ eventId: row.eventId }}
                        data-testid="player-game-log-link"
                        className={`flex min-h-touch-target items-center gap-2 text-ink no-underline after:absolute after:inset-0 after:content-[''] ${FOCUS_RING}`}
                      >
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate">{primary}</span>
                          {secondary !== null ? (
                            <span className="text-small text-ink-secondary tabular-nums">
                              {secondary}
                            </span>
                          ) : null}
                        </span>
                        <ChevronRightIcon
                          className="text-ink-tertiary"
                          testId="player-game-log-chevron"
                        />
                      </Link>
                    </th>
                    {table.columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          STAT_CELL_CLASS,
                          ROW_RULE_CLASS,
                          "bg-surface text-ink",
                        )}
                      >
                        {gameLogCellText(row.cells, column.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isEndClipped ? (
          <div
            aria-hidden="true"
            data-testid="player-game-log-scroll-cue-end"
            className="pointer-events-none absolute inset-y-0 right-0 z-20 w-8 bg-linear-to-l from-surface to-transparent"
          />
        ) : null}
      </div>
    </section>
  );
};
